import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {DataARLedgerListModel} from '../../models/data-ar-ledger-list.model';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ARService} from '../../ar.service';
import {DataArPaymentAuditModel} from '../../models/data-ar-payment-audit.model';
import {DataARPaymentDistribsListModel} from '../../models/data-ar-payment-distribs-list.model';
import { Router } from '@angular/router';
import {NoteDataModel} from '../../../../models/notes/note-data.model';
import {NotesService} from '../../../../services/notes.service';
import { PrintCCReceiptModel } from 'src/app/models/accountsReceivable/PrintCCReceipt.model';
import { Globals } from 'src/app/globals';
import { TDOPrintService } from 'src/app/services/print/tdoprint.service';
import { ReportIDEnum } from 'src/app/models/enum/report-ID-enum';
import { ConversionUtilities } from 'src/app/utilities/conversion.utilities';
import {ArPaymentDistributionsComponent} from '../ar-payment-distributions/ar-payment-distributions.component';
import { UiService } from '../../../../services/ui.service';
import { EnumARPaymentType } from 'src/app/models/enum/enum-ar-payment-type';

@Component({
  standalone: false,
  selector: 'app-ar-payment-details',
  templateUrl: './ar-payment-details.component.html',
  styleUrls: ['./ar-payment-details.component.scss']
})
export class ArPaymentDetailsComponent implements OnInit {
  isLoading: boolean = false;
  ledgerItem: DataARLedgerListModel;
  paymentId: string;
  payment: DataArPaymentAuditModel;
  paymentDistributions: DataARPaymentDistribsListModel[] = [];
  notes: NoteDataModel[] = [];

  enumARLedgerItemOriginTypeTransaction: string = '00000207-1000-0000-0000-000000000003';
  enumARLedgerItemOriginTypePatient: string = '00000207-1000-0000-0000-000000000002';
  enumARLedgerItemOriginTypeInsurance: string = '00000207-1000-0000-0000-000000000001';
  showReceipt: boolean = false;
  isPrintingReceipt: boolean =false;
  TZID: string = '';
  constructor(
    private readonly router: Router,
    public dialogRef: MatDialogRef<ArPaymentDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly arService: ARService,
    private readonly notesService: NotesService,
    private readonly globals: Globals,
    private readonly printService: TDOPrintService,
    private dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly uiService: UiService,
  ) { }

  ngOnInit(): void {
    this.ledgerItem = this.data.ledgerItem;
    this.paymentId = this.data.PaymentID;
    this.init();
    this.getNotes();
  }

  async init() {
    this.isLoading = true;
    const promises: any[] = [];
    promises.push(this.arService.getPaymentDistributionsForPaymentID(this.paymentId, this.ledgerItem.PatientId).toPromise());

    promises.push(this.arService.getPaymentRecordResolved(this.paymentId).toPromise());

    try {
      const results: any[] = await Promise.all(promises);
      this.paymentDistributions = results[0];
      this.payment = results[1];
      this.showReceipt = this.shouldShowReceipt(this.payment);

      this.isLoading = false;
      this.cdr.detectChanges();
    }
    catch (err) {
      console.error(err);
      this.createMessage('Something went wrong getting Payment details', 'Error');
      this.isLoading = false;
    }
  }

  async getNotes() {
    try {
      const promises: any[] = [];
      promises.push(this.notesService.getNotesForLedgerItem(this.ledgerItem.LedgerID).toPromise());
      const results: any[] = await Promise.all(promises);
      this.notes = results[0];
    }
    catch (err) {
      console.error(err);
      this.createMessage('Something went wrong looking for notes', 'Error');
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Determines whether the "Print Receipt" button should be shown for a payment.
   *
   * Only online, integrated (TDO Payments) credit-card payments have a processed
   * credit-card transaction/receipt to print. Offline/manual credit-card payments
   * (EnumARPaymentType.CreditCardOffline) share the "Credit Card" display string but
   * have no receipt, so gating on the display string alone would incorrectly show the
   * button for them. Discriminate on the payment-type enum instead.
   *
   * Gate solely on the payment-type enum: integrated CC processing is triggered by
   * enumARPayementTypeID === CreditCard (see ar-payment.component), and a payment
   * method's ePay type can be cleared to null while still being an integrated CC
   * payment, so requiring the ePay type would wrongly hide the button for valid
   * integrated payments.
   */
  shouldShowReceipt(payment?: DataArPaymentAuditModel | null): boolean {
    return !!payment
      && payment.enumARPayementTypeID === EnumARPaymentType.CreditCard;
  }

  close() {
    this.dialogRef.close();
  }

  createMessage(message: string, type: string) {
    this.uiService.displayMessage(type, message);
  }

  async openPaymentReceipt(){
    this.isPrintingReceipt = true;
    this.cdr.detectChanges();
    const paymentPrintParameters = new PrintCCReceiptModel();
    paymentPrintParameters.PatientID = this.ledgerItem.PatientId;
    paymentPrintParameters.EnterpriseID = localStorage.getItem('TDOEnterpriseId');
    paymentPrintParameters.LOCID = this.globals.LOCID;
    paymentPrintParameters.TDOCID = localStorage.getItem('TDOCID');
    paymentPrintParameters.PaymentID = this.paymentId;
    paymentPrintParameters.LedgerID = this.ledgerItem.LedgerID;

    try {
      const documentID = await this.printService.printReport(ReportIDEnum.reportID_CreditCardReceiptReport, paymentPrintParameters).toPromise();
      await this.printService.executePrint([documentID]);
    }
    catch (error) {
      this.uiService.displayMessage('Error', 'Error when trying to print Document.');
    }
    finally {
      this.isPrintingReceipt = false;
      this.cdr.detectChanges();
    }
  }

  convertUTCDatetoTimezone(date: Date, tz: string) {
      date = ConversionUtilities.getAdjustedTimezoneDate(date, tz);
      return date;
  }

  openPaymentDistributionDetailsWindow(item: DataARLedgerListModel) {
    // console.log('openPaymentDistributionDetailsWindow', item);
    const dialogRef = this.dialog.open(ArPaymentDistributionsComponent, {
      panelClass: 'dialog',
      autoFocus: false,
      data: {
        PaymentID: item.PaymetnID,
        ledgerItem: item,
      }
    });
    const paymentDetailsSub = dialogRef.afterClosed().subscribe(result => {

    }, err => {
      console.error(err);
      this.createMessage('Something went wrong opening payment distribution details', 'Error');
      this.isLoading = false;
      this.cdr.detectChanges();
    });
  }
}
